/**
 * Browser/worker tree-sitter loader. Owns the bundler-specific bits the core
 * stays free of: Vite `?url` WASM imports, one-time `Parser.init`, and per-
 * language parser/query caches. The pure extraction algorithm lives in
 * `@slate/core` (extractSymbolsFromTree).
 */
import { Parser, Language, Query } from 'web-tree-sitter';
import treeSitterWasmUrl from 'web-tree-sitter/tree-sitter.wasm?url';
import tsWasmUrl from 'tree-sitter-wasms/out/tree-sitter-typescript.wasm?url';
import tsxWasmUrl from 'tree-sitter-wasms/out/tree-sitter-tsx.wasm?url';
import jsWasmUrl from 'tree-sitter-wasms/out/tree-sitter-javascript.wasm?url';
import {
  queryFor,
  extractSymbolsFromTree,
  type SymbolLanguageId,
  type CodeSymbol,
} from '@slate/core';

const WASM_URL: Record<SymbolLanguageId, string> = {
  typescript: tsWasmUrl,
  tsx: tsxWasmUrl,
  javascript: jsWasmUrl,
};

let initPromise: Promise<void> | null = null;
const parserCache = new Map<SymbolLanguageId, Parser>();
const queryCache = new Map<SymbolLanguageId, Query>();

function ensureInit(): Promise<void> {
  if (!initPromise) {
    initPromise = Parser.init({ locateFile: () => treeSitterWasmUrl });
  }
  return initPromise;
}

async function parserFor(lang: SymbolLanguageId): Promise<Parser> {
  let parser = parserCache.get(lang);
  if (parser) return parser;
  await ensureInit();
  const language = await Language.load(WASM_URL[lang]);
  parser = new Parser();
  parser.setLanguage(language);
  parserCache.set(lang, parser);
  queryCache.set(lang, new Query(language, queryFor(lang)));
  return parser;
}

/** Parse `source` and return its outline. Trees are freed after extraction. */
export async function extractSymbols(
  source: string,
  lang: SymbolLanguageId,
): Promise<CodeSymbol[]> {
  const parser = await parserFor(lang);
  const query = queryCache.get(lang)!;
  const tree = parser.parse(source);
  // `parse` returns `Tree | null` in 0.25.10. A null tree means the source
  // could not be parsed at all, for which the correct outline is empty.
  if (!tree) return [];
  try {
    return extractSymbolsFromTree(tree, query);
  } finally {
    tree.delete();
  }
}
